import { Flame } from 'lucide-react'
import type { HabitBoardCard, HabitOutcome } from '../../services/habitService'
import { Timestamp } from '../ui/Timestamp'

/**
 * How a habit has been going, in one glance (Habits UX pass 2026-09-06):
 * the last fourteen days as dots, the run in words, the best run, the last
 * time. Tone: what happened, never a scolding — a miss is "not this time".
 */
const DOT: Record<string, string> = {
  done: 'bg-ok',
  skipped: 'bg-soft',
  missed: 'bg-warn/50',
  none: 'border border-soft bg-transparent',
}
const WORD: Record<string, string> = { done: 'done', skipped: 'not today', missed: 'not this time' }

/** The strip starts the day the item began (owner, 2026-09-12), so it is
 *  fourteen days long only once the item has lived fourteen days. */
export function stripLabel(n: number): string {
  if (n >= 14) return 'The last fourteen days'
  if (n <= 1) return 'Since today'
  return `The ${n} days since it began`
}

export function Last14Strip({ days, size = 'md' }: { days: Array<{ date: string; outcome: HabitOutcome | null }>; size?: 'sm' | 'md' }) {
  const dim = size === 'sm' ? 'h-2 w-2' : 'h-2.5 w-2.5'
  return (
    <span className="inline-flex items-center gap-1" aria-label={stripLabel(days.length)} title={stripLabel(days.length)} data-testid="habit-last-14">
      {days.map((d) => (
        <span key={d.date} title={`${d.date} · ${d.outcome ? WORD[d.outcome] : 'not on the plan'}`} className={`${dim} rounded-full ${DOT[d.outcome || 'none']}`} />
      ))}
    </span>
  )
}

export function HabitKeepingUp({ card, headingClass }: { card: HabitBoardCard | null; headingClass: string }) {
  if (!card) return null
  const lastOutcome = [...card.last_14].reverse().find((d) => d.outcome)?.outcome || null
  return (
    <section data-testid="habit-keeping-up">
      <h3 className={headingClass}><Flame className="h-3.5 w-3.5 text-brand" /> Keeping up</h3>
      <div className="mt-2"><Last14Strip days={card.last_14} /></div>
      <p className="mt-2 text-sm text-primary" data-testid="habit-keeping-up-words">
        {card.streak >= 2 ? `${card.streak} in a row` : card.streak === 1 ? 'One so far' : lastOutcome === 'missed' ? 'Fresh start' : card.chances_30 ? 'Finding the rhythm' : 'The first one is still ahead'}
        {card.chances_30 > 0 && <span className="text-secondary"> · {card.done_30} of the last {card.chances_30}</span>}
      </p>
      <dl className="mt-1 space-y-0.5 text-app-meta text-secondary">
        {card.best_streak > card.streak && card.best_streak >= 2 && <div>Best run {card.best_streak}</div>}
        {card.last_done && <div>Last time <Timestamp value={`${card.last_done}T12:00:00`} mode="date" /></div>}
        {/* The next time is the Plan block's (2026-09-12): Keeping up is the past. */}
      </dl>
    </section>
  )
}

export default HabitKeepingUp
